
import React, { useState, useMemo } from "react";
import DataTable from "components/DataTable";
import PageWrapper from "components/PageWrapper";
import { useTableData } from "../../hooks/useTableData";
import { walletReportsConfig } from "../../config/tableConfigs";
import { useSession } from "hooks/useSession";

const WalletReportsPage = () => {
  const {userid, role} = useSession();
  const userRole = role === "Platform User" ? "Admin" : role;
  // Default API URL (without hardcoded invalid dates)
   const getLast7Days = () => {
    const today = new Date();
    const last7 = new Date();
    last7.setDate(today.getDate() - 1)  ;
  
    return {
      start: last7.toISOString().split("T")[0],
      end: today.toISOString().split("T")[0],
    };
  };
  
const getTodayRange = () => {
  const now    = new Date();
  const offset = now.getTimezoneOffset(); // minutes behind UTC
  const local  = new Date(now.getTime() - offset * 60 * 1000);
  const today  = local.toISOString().split("T")[0];
  return { start: today, end: today };
};

const { start, end } = getTodayRange();
  
  const [startDate, setStartDate] = useState(start);
  const [endDate, setEndDate] = useState(end);
  
  const [apiUrl, setApiUrl] = useState(
    `https://pro.mokshiq.in/main_wallet_report?user=${userid}&filter_date=${start}&end_date=${end}`
  );

  // Example: set this dynamically after login if needed
 

  const tableData = useTableData(apiUrl, walletReportsConfig);

  // Called by PageWrapper when date range changes
  const handleDateFilter = (startDate, endDate) => {
    console.log("Date Filter Applied:", startDate, "→", endDate);
    const newUrl = `https://pro.mokshiq.in/main_wallet_report?user=${userid}&filter_date=${startDate}&end_date=${endDate}`;
    setApiUrl(newUrl);
  };

  // === Role-based column filtering ===
  const filteredConfig = useMemo(() => {
    const baseConfig = { ...walletReportsConfig };

    const roleColumnMap = {
      "Retailer": [
        "id",
        "order_id",
        "product",
        "amount",
        "status",
        "type",
        "opening_balance",
        "closing_balance",
        "commission",
        "tds",
        "charge",
        "created_at",
      ],
      "API Partner": [
        "id",
        "order_id",
        "product",
        "amount",
        "status",
        "type",
        "opening_balance",
        "closing_balance",
        "commission",
        "tds",
        "charge",
        "created_at",
      ],
      "White label": [
    "ID",
    "created_at",
    "BC Name",
    "Product",
    "Amount",
    "Status",
    "Opening Balance BC",
    "BC Commission",
    "BC TDS",
    "BC Charge",
    "Closing Balance BC",
    "Customer No",
    "TXN Flow",
    "REF No",
    "ACK NO",
    "TXN Date",
    "TXN Time"
  ],
      "Distributer": [
        "id",
        "order_id",
        "product",
        "amount",
        "status",
        "type",
        "commission",
        "tds",
        "charge",
        "created_at",
      ],
     "Admin": [
  "ID",
  "created_at",
  "modified_at",
  "BC Name",
  "Product Type",
  "Product",
  "Amount",
  "Commission",
  "TDS",
  "Charge",
  "Opening Balance",
  "Closing balance",          // ⚠️ lowercase 'b'
  "SDOpening Balance",
  "SDCommission",
  "SDTDS",
  "SDCharge",
  "SDClosing balance",        // ⚠️ lowercase 'b'
  "DOpening Balance",
  "DCommission",
  "DTDS",
  "DCharge",
  "DClosing balance",         // ⚠️ lowercase 'b'
  "Opening Balance BC",
  "BC Commission",
  "BC TDS",
  "BC Charge",
  "Closing Balance BC",       // ✅ uppercase 'B'
  "Customer No",
  "Status",
  "Wallet Txn",
  "TXN Flow",
  "REF No",
  "ACK NO",
  "TXN ID",
  "bank_rrn",
  "aadhar_number",
  "TXN Date",
  "TXN Time",
],
     "Main Admin": [
  "id",
  "modified_at",
  "BC_ID",
  "BC Name",
  "Transection Type",
  "Product",

  "Opening Balance MAdmin",
  "Amount",
  "Commission",
  "Charge",
  "TDS",
  "Closing balance MAdmin",

  "Opening Balance Admin",
  "ACommission",
  "ATDS",
  "ACharge",
  "AGST",
  "Closing Balance Admin",

  "Opening Balance BC",
  "BC Commission",
  "BC TDS",
  "BC Charge",
  "Closing Balance BC",

  "Customer No",
  "Status",
  "Wallet Txn",
  "TXN Flow",
  "REF No",
  "ACK NO",
  "bank_rrn",
  "aadhar_number",
  "created_at",
  "TXN Date",
  "TXN Time",
  "TXN ID",
  "Admin"
]

    };

    const allowed = roleColumnMap[userRole] || [];
    baseConfig.columns = walletReportsConfig.columns.filter((col) =>
      allowed.includes(col.key)
    );

    return baseConfig;
  }, [userRole]);

  // Summary metrics calculation
  const summaryMetrics = useMemo(() => {
    const list = tableData.data || [];
    let opening = 0;
    let closing = 0;
    let credit = 0;
    let debit = 0;
    let creditCount = 0;
    let debitCount = 0;

    if (list.length > 0) {
      opening = parseFloat(list[0].opening_balance || list[0]["Opening Balance"] || list[0]["Opening Balance BC"] || 0);
      closing = parseFloat(list[list.length - 1].closing_balance || list[list.length - 1]["Closing balance"] || list[list.length - 1]["Closing Balance BC"] || 0);

      list.forEach((item) => {
        const type = (item.type || item.type_name || item.type_str || "").toLowerCase();
        const amt = parseFloat(item.amount || item.Amount || 0);
        if (type.includes("credit") || type.includes("load")) {
          credit += amt;
          creditCount++;
        } else if (type.includes("debit")) {
          debit += amt;
          debitCount++;
        }
      });
    }

    return { opening, closing, credit, debit, creditCount, debitCount };
  }, [tableData.data]);

  // === Table action handlers ===
  const handleView = (report) => {
    console.log("Viewing report:", report);
  };

  const handleEdit = (report) => {
    console.log("Editing report:", report);
  };

  const handleDelete = (report) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      console.log("Deleting report:", report);
    }
  };

  const handleExport = (selectedIds) => {
    console.log("Exporting selected data:", selectedIds);
    alert(`Exporting ${selectedIds.length} selected records`);
  };

  const handleDeleteSelected = (selectedIds) => {
    if (
      window.confirm(`Are you sure you want to delete ${selectedIds.length} records?`)
    ) {
      console.log("Deleting selected records:", selectedIds);
    }
  };

  return (
    <main style={{ backgroundColor: "#f8fafc", minHeight: "100vh", paddingBottom: "40px" }}>
      <div className="container-fluid px-4 py-3">
        {/* Top Header Title Bar & Right User Avatar Button */}
        <div className="row align-items-center mb-3">
          <div className="col d-flex align-items-center justify-content-between">
            <div>
              <h3 className="fw-extrabold mb-1" style={{ color: "#0f172a", fontSize: "24px", letterSpacing: "-0.5px" }}>
                Wallet Reports
              </h3>
              <ul className="d-flex align-items-center gap-2 list-unstyled mb-0" style={{ fontSize: "13.5px" }}>
                <li>
                  <a className="text-decoration-none d-flex align-items-center gap-1.5" href="#" style={{ color: "#64748b", fontWeight: "500" }}>
                    <i className="ph-duotone ph-house-line" style={{ fontSize: "16px", color: "#7e57c2" }}></i>
                  </a>
                </li>
                <li style={{ color: "#94a3b8" }}>/</li>
                <li style={{ color: "#64748b", fontWeight: "500" }}>Reports</li>
                <li style={{ color: "#94a3b8" }}>/</li>
                <li className="fw-bold" style={{ color: "#7e57c2" }}>
                  Wallet Reports
                </li>
              </ul>
            </div>

            <div>
              <button
                className="btn d-flex align-items-center justify-content-center p-0 border-0 text-white shadow-sm"
                style={{
                  backgroundColor: "#7e57c2",
                  borderRadius: "14px",
                  width: "44px",
                  height: "44px",
                  boxShadow: "0 4px 12px rgba(126, 87, 194, 0.3)"
                }}
              >
                <i className="ph-bold ph-user" style={{ fontSize: "20px" }}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Hero Banner Card with 3D Wallet Illustration Graphic */}
        <div
          className="card border-0 mb-3 position-relative overflow-hidden shadow-sm"
          style={{
            background: "linear-gradient(135deg, #f5efff 0%, #eef2ff 60%, #e0e7ff 100%)",
            borderRadius: "16px"
          }}
        >
          <div className="card-body p-3 px-4 position-relative z-1">
            <div className="row align-items-center">
              <div className="col-12 col-md-7 col-lg-8">
                <div className="d-flex align-items-center gap-2.5">
                  <div
                    className="d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "12px",
                      backgroundColor: "#f3e8ff",
                      color: "#7e57c2"
                    }}
                  >
                    <i className="ph-duotone ph-wallet" style={{ fontSize: "22px" }}></i>
                  </div>
                  <div>
                    <h5 className="fw-extrabold mb-0.5" style={{ color: "#0f172a", fontSize: "16px" }}>
                      Wallet Reports
                    </h5>
                    <p className="text-muted mb-0" style={{ fontSize: "12.5px" }}>
                      View and analyse your wallet transactions, balance and deductions.
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-5 col-lg-4 text-end d-none d-md-block">
                <img
                  src="../assets/images/service/wallet-reports-banner.png"
                  onError={(e) => { e.target.src = "/assets/images/service/wallet-reports-banner.png"; }}
                  alt="Wallet Reports Banner"
                  className="img-fluid"
                  style={{
                    maxHeight: "65px",
                    objectFit: "contain",
                    mixBlendMode: "multiply"
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Date Filter Bar Card */}
        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "20px", backgroundColor: "#ffffff" }}>
          <div className="card-body p-4">
            <form onSubmit={(e) => { e.preventDefault(); handleDateFilter(startDate, endDate); }}>
              <div className="row g-3 align-items-end">
                <div className="col-12 col-md-5">
                  <label className="small fw-semibold text-muted mb-1.5" style={{ fontSize: "13px" }}>From Date</label>
                  <input
                    type="date"
                    className="form-control py-2 px-3 border-1"
                    style={{ borderRadius: "12px", borderColor: "#e2e8f0", fontSize: "14px" }}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>

                <div className="col-12 col-md-5">
                  <label className="small fw-semibold text-muted mb-1.5" style={{ fontSize: "13px" }}>To Date</label>
                  <input
                    type="date"
                    className="form-control py-2 px-3 border-1"
                    style={{ borderRadius: "12px", borderColor: "#e2e8f0", fontSize: "14px" }}
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>

                <div className="col-12 col-md-2">
                  <button
                    type="submit"
                    className="btn border-0 text-white d-flex align-items-center justify-content-center gap-2 px-3 py-2 fw-semibold shadow-sm w-100"
                    style={{
                      borderRadius: "12px",
                      fontSize: "13.5px",
                      backgroundColor: "#7e57c2"
                    }}
                  >
                    <i className="ph-bold ph-magnifying-glass" style={{ fontSize: "16px" }}></i>
                    <span>Submit</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Data Table Container */}
        <div className="row advance-table-section">
          <div className="col-12">
            <DataTable
              config={filteredConfig}
              tableData={tableData}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onExport={handleExport}
              onDeleteSelected={handleDeleteSelected}
            />
          </div>
        </div>
      </div>
    </main>
  );
};

export default WalletReportsPage;
